// Analytics Event Tracking Utility
// Simple wrapper around Mixpanel

import mixpanel from '../config/mixpanel';

export interface AnalyticsEvent {
  name: string;
  properties?: Record<string, any>;
  userId?: string;
  timestamp?: number;
}

export interface AnalyticsUser {
  id: string;
  email?: string;
  name?: string;
  properties?: Record<string, any>;
}

class AnalyticsTracker {
  private user: AnalyticsUser | null = null;

  // Track an event
  public track(event: AnalyticsEvent): void {
    try {
      const eventData = {
        ...event,
        timestamp: event.timestamp || Date.now(),
        userId: event.userId || this.user?.id,
      };

      if (!event.name) {
        console.error('Event name is required');
        return;
      }

      mixpanel.track(event.name, event.properties);
      //console.log('Event tracked:', eventData);
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  // Identify user
  public identify(user: AnalyticsUser): void {
    this.user = user;

    try {
      mixpanel.identify(user.id);
      if (user.properties) {
        mixpanel.people.set(user.properties);
      }
      console.log('User identified:', user);
    } catch (error) {
      console.error('Failed to identify user:', error);
    }
  }

  // Set user properties
  public setUserProperties(properties: Record<string, any>): void {
    try {
      mixpanel.people.set(properties);
    } catch (error) {
      console.error('Failed to set user properties:', error);
    }
  }

  // Reset user (logout)
  public reset(): void {
    this.user = null;

    try {
      mixpanel.reset();
      console.log('Analytics reset');
    } catch (error) {
      console.error('Failed to reset analytics:', error);
    }
  }

  // Check if Mixpanel is ready
  public isReady(): boolean {
    return !!process.env.REACT_APP_MIXPANEL_TOKEN;
  }

  // Get Mixpanel status for debugging
  public getStatus() {
    return {
      token: process.env.REACT_APP_MIXPANEL_TOKEN ? 'Set' : 'Not set',
      user: this.user ? this.user.id : null,
      ready: this.isReady(),
    };
  }
}

// Export the analytics tracker
export default AnalyticsTracker;

// Predefined event types for common actions
export const AnalyticsEvents = {
  // User actions
  USER_SIGNUP: 'user_signup',
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  USER_PROFILE_UPDATE: 'user_profile_update',

  // Product actions
  PRODUCT_VIEW: 'product_view',
  PRODUCT_ADD_TO_CART: 'product_add_to_cart',
  PRODUCT_REMOVE_FROM_CART: 'product_remove_from_cart',
  PRODUCT_PURCHASE: 'product_purchase',

  // Collection actions
  COLLECTION_VIEW: 'collection_view',
  COLLECTION_CREATE: 'collection_create',
  COLLECTION_UPDATE: 'collection_update',
  COLLECTION_DELETE: 'collection_delete',

  // Order actions
  ORDER_CREATED: 'order_created',
  ORDER_CONFIRMED: 'order_confirmed',
  ORDER_SHIPPED: 'order_shipped',
  ORDER_DELIVERED: 'order_delivered',
  ORDER_CANCELLED: 'order_cancelled',

  // Page views
  PAGE_VIEW: 'page_view',
  PAGE_LOAD: 'page_load',

  // Auth
  OTP_REQUESTED: 'otp_requested',
  OTP_VERIFIED: 'otp_verified',

  // Store setup journey
  STORE_CREATED: 'store_created',

  // Landing Page
  LANDING_PAGE_VIEW: 'landing_page_view',
  LANDING_PAGE_ATTRIBUTION_CAPTURED: 'landing_page_attribution_captured',
  LOGIN_PAGE_VIEW: 'login_page_view',
  DASHBOARD_PAGE_VIEW: 'dashboard_page_view',
  PRODUCTS_PAGE_VIEW: 'products_page_view',
  ONBOARDING_PAGE_VIEW: 'onboarding_page_view',

  // Feature usage
  FEATURE_USED: 'feature_used',
  BUTTON_CLICK: 'button_click',
  CTA_CLICKED: 'cta_clicked',
  FORM_SUBMIT: 'form_submit',
  SEARCH_PERFORMED: 'search_performed',

  // Error tracking
  ERROR_OCCURRED: 'error_occurred',
  API_ERROR: 'api_error',
  VALIDATION_ERROR: 'validation_error',
} as const;

// Helper function to create analytics instance
export function createAnalytics(): AnalyticsTracker {
  return new AnalyticsTracker();
} 
